import React, { useCallback, useMemo, useState } from 'react';

import { useSnackbar } from 'notistack';
import { useDropzone } from 'react-dropzone';
import dayjs from '~/timeInit';

import UploadFileIcon from '@mui/icons-material/UploadFile';
import {
  Alert,
  AlertTitle,
  Autocomplete,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  LinearProgress,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

import { useUploadCostPrices } from '~/hooks/apis/tours/useTourCostPrices';
import { useGetTourDepartures } from '~/hooks/apis/tours/useTourDepartures';

import { SlidingScaleByDeparturePricingItem, TourService } from '~/types/tours/costPrice';

import UploadPreviewTable from './UploadPreviewTable';
import { mergePricingItems, resolveUploadedDepartures, summariseMerge } from './departureRows';
import {
  ParsedSlidingScaleRow,
  buildPricingCsv,
  buildPricingFilename,
  buildTemplateCsv,
  buildTemplateFilename,
  downloadCsv,
  parseSlidingScaleCsv,
} from './slidingScaleCsv';

/** Enough of the sheet to check it parsed as expected, without listing it all. */
const MAX_PREVIEW_ROWS = 20;

interface Props {
  open: boolean;
  service: TourService;
  /** How many sliding scale rows the service already has, if any. */
  existingRowCount?: number;
  /**
   * Prices this service keys by departure, so the sheet carries a Departure
   * column and the upload is sent as the by-departure kind.
   */
  byDeparture?: boolean;
  /** The tour being priced, so its departures can be offered to choose from. */
  tourId?: string;
  /** Departures to pre-fill the template with, one row per package option. */
  departures?: Array<string>;
  /**
   * What the service already holds. An upload replaces a package option's
   * stored pricing rather than merging into it, so a sheet covering one
   * departure is folded into these and the union is sent.
   */
  existingItems?: Array<SlidingScaleByDeparturePricingItem>;
  onClose: () => void;
}

export default function SlidingScaleUploadDialog({
  open,
  service,
  existingRowCount = 0,
  byDeparture = false,
  tourId,
  departures,
  existingItems = [],
  onClose,
}: Props) {
  const { enqueueSnackbar } = useSnackbar();

  const [fileName, setFileName] = useState<string | null>(null);
  const [parseErrors, setParseErrors] = useState<Array<string>>([]);
  const [rows, setRows] = useState<Array<ParsedSlidingScaleRow>>([]);

  const uploadCostPrices = useUploadCostPrices({ supplierId: service.supplierId, serviceId: service.id });

  const { data: tourDepartures = [], isLoading: isLoadingDepartures } = useGetTourDepartures(
    byDeparture ? tourId : undefined,
  );

  /**
   * The departure a sheet is for, so its rows do not each have to name it.
   * Editing one departure arrives with it already chosen.
   */
  /** The chosen departure's id, which is what cost prices are keyed by. */
  const [selectedDeparture, setSelectedDeparture] = useState<string>(departures?.length === 1 ? departures[0] : '');

  const scope = useMemo(
    () => ({
      byDeparture,
      departures: selectedDeparture ? [selectedDeparture] : departures,
      defaultDeparture: selectedDeparture || undefined,
    }),
    [byDeparture, departures, selectedDeparture],
  );

  const reset = useCallback(() => {
    setFileName(null);
    setParseErrors([]);
    setRows([]);
  }, []);

  const handleClose = useCallback(() => {
    reset();
    onClose();
  }, [onClose, reset]);

  const onDrop = useCallback((files: Array<File>) => {
    const file = files[0];
    if (!file) {
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      const parsed = parseSlidingScaleCsv(String(reader.result ?? ''), scope);
      setFileName(file.name);
      setParseErrors(parsed.errors);
      setRows(parsed.rows);
    };
    reader.onerror = () => {
      setParseErrors(['Could not read the selected file']);
    };
    reader.readAsText(file);
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    multiple: false,
    accept: {
      'text/csv': ['.csv'],
      'application/vnd.ms-excel': ['.csv'],
    },
  });

  /**
   * svc-tour takes the whole sheet in one request and resolves package names
   * against the service's configurations, so rows are sent together rather than
   * one request per row.
   */
  /**
   * Sheets name departures because nobody types a uuid, but prices are keyed by
   * id, so every row is resolved before anything is sent.
   */
  const { uploadedItems, resolutionErrors } = useMemo(() => {
    const uploadedRows = rows as Array<{ item: SlidingScaleByDeparturePricingItem; rowNumber: number }>;

    if (!byDeparture) {
      return { uploadedItems: uploadedRows.map(({ item }) => item), resolutionErrors: [] as Array<string> };
    }

    const { items, errors } = resolveUploadedDepartures(tourDepartures, uploadedRows);

    return { uploadedItems: items, resolutionErrors: errors };
  }, [byDeparture, rows, tourDepartures]);

  /** The union actually sent, so departures the sheet omits survive. */
  const mergedPricing = useMemo(() => mergePricingItems(existingItems, uploadedItems), [existingItems, uploadedItems]);

  const mergeSummary = useMemo(() => summariseMerge(existingItems, uploadedItems), [existingItems, uploadedItems]);

  const handleUpload = useCallback(async () => {
    if (!rows.length) {
      return;
    }

    try {
      await uploadCostPrices.mutateAsync(
        byDeparture
          ? { kind: 'SlidingScaleByDeparture', pricing: mergedPricing }
          : { kind: 'SlidingScale', pricing: rows.map((row) => row.item) },
      );

      enqueueSnackbar(`Uploaded ${rows.length} cost price row${rows.length > 1 ? 's' : ''}`, { variant: 'success' });
      handleClose();
    } catch (error) {
      enqueueSnackbar(`Upload failed: ${error?.message ?? 'Unknown error'}`, {
        variant: 'error',
        autoHideDuration: 10000,
      });
    }
  }, [byDeparture, enqueueSnackbar, handleClose, mergedPricing, rows, uploadCostPrices]);

  /**
   * Editing one departure downloads just that departure's rates, so the sheet
   * is what was clicked rather than the whole service.
   */
  const downloadItems = useMemo(() => {
    if (departures?.length === 1) {
      return existingItems.filter((item) => item.departure === departures[0]);
    }
    return existingItems;
  }, [departures, existingItems]);

  /** Confirms the chosen name is a real departure, by naming its date. */
  const selectedTourDeparture = useMemo(
    () => tourDepartures.find((departure) => departure.departureId === selectedDeparture),
    [selectedDeparture, tourDepartures],
  );

  const allErrors = useMemo(() => parseErrors.concat(resolutionErrors), [parseErrors, resolutionErrors]);

  // Choosing a departure is a step only a by-departure service has.
  const steps = byDeparture
    ? { departure: '1.', sheet: '2.', upload: '3.' }
    : { departure: '', sheet: '1.', upload: '2.' };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="lg" fullWidth>
      <DialogTitle>{existingRowCount > 0 ? 'Re-upload' : 'Upload'} sliding scale cost prices</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={3}>
          {byDeparture && (
            <Stack spacing={1}>
              <Typography variant="subtitle2">{steps.departure} Which departure</Typography>
              <Autocomplete
                options={tourDepartures}
                value={selectedTourDeparture ?? null}
                onChange={(_event, next) => setSelectedDeparture(next?.departureId ?? '')}
                getOptionLabel={(option) => option.departureName ?? ''}
                isOptionEqualToValue={(option, value) => option.departureId === value.departureId}
                renderOption={(props, option) => (
                  <Box component="li" {...props} key={option.departureId}>
                    <Stack>
                      <Typography variant="body2">{option.departureName}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {dayjs(option.departureDate).format('D MMM YYYY')}
                      </Typography>
                    </Stack>
                  </Box>
                )}
                loading={isLoadingDepartures}
                sx={{ maxWidth: 420 }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Departure"
                    helperText={
                      selectedTourDeparture
                        ? `Leaves ${dayjs(selectedTourDeparture.departureDate).format('D MMM YYYY')}. The sheet needs no Departure column.`
                        : 'Choose one and the sheet needs no Departure column'
                    }
                  />
                )}
              />
            </Stack>
          )}

          <Stack spacing={1}>
            <Typography variant="subtitle2">{steps.sheet} Start from a sheet</Typography>
            <Typography variant="body2" color="text.secondary">
              {byDeparture && !selectedDeparture
                ? "Pre-filled with this service's packages. Name a departure on every row, and fill in a row per season."
                : "Pre-filled with this service's packages. Fill in a row per season."}
            </Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              <Button
                variant="outlined"
                size="small"
                onClick={() => downloadCsv(buildTemplateFilename(service, scope), buildTemplateCsv(service, scope))}
              >
                Download template
              </Button>
              {!!downloadItems.length && (
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() =>
                    downloadCsv(buildPricingFilename(service, scope), buildPricingCsv(downloadItems, scope))
                  }
                >
                  Download current prices
                </Button>
              )}
            </Stack>
          </Stack>

          <Stack spacing={1}>
            <Typography variant="subtitle2">{steps.upload} Upload it back</Typography>
            <Box
              {...getRootProps()}
              sx={{
                p: 4,
                border: '1px dashed',
                borderColor: isDragActive ? 'primary.main' : 'divider',
                borderRadius: 1,
                textAlign: 'center',
                cursor: 'pointer',
                bgcolor: isDragActive ? 'action.hover' : 'background.default',
              }}
            >
              <input {...getInputProps()} />
              <UploadFileIcon color="action" />
              <Typography variant="body2">
                {fileName ?? 'Drop a cost price CSV here, or click to choose a file'}
              </Typography>
            </Box>
          </Stack>

          {!byDeparture && existingRowCount > 0 && (
            <Alert severity="warning">
              This service already has {existingRowCount} sliding scale row{existingRowCount === 1 ? '' : 's'}.
              Uploading replaces the pricing for any package and currency in the sheet, including seasons the sheet
              leaves out.
            </Alert>
          )}

          {byDeparture && !!rows.length && (
            <Alert severity="info">
              {[
                mergeSummary.added.length ? `Adds ${mergeSummary.added.join(', ')}` : '',
                mergeSummary.replaced.length ? `Replaces ${mergeSummary.replaced.join(', ')}` : '',
                mergeSummary.untouched.length
                  ? `Leaves ${mergeSummary.untouched.length} other departure${
                      mergeSummary.untouched.length > 1 ? 's' : ''
                    } as they are`
                  : '',
              ]
                .filter(Boolean)
                .join('. ')}
              .
            </Alert>
          )}

          {uploadCostPrices.isPending && <LinearProgress />}

          {!!allErrors.length && (
            <Alert severity="warning">
              <AlertTitle>{allErrors.length} row(s) skipped</AlertTitle>
              <Stack component="ul" sx={{ pl: 2, m: 0 }}>
                {allErrors.slice(0, 10).map((error) => (
                  <li key={error}>
                    <Typography variant="body2">{error}</Typography>
                  </li>
                ))}
              </Stack>
              {allErrors.length > 10 && <Typography variant="body2">…and {allErrors.length - 10} more</Typography>}
            </Alert>
          )}

          <UploadPreviewTable rows={rows} limit={MAX_PREVIEW_ROWS} />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button variant="contained" onClick={handleUpload} disabled={!rows.length || uploadCostPrices.isPending}>
          Upload {rows.length || ''} row{rows.length === 1 ? '' : 's'}
        </Button>
        <Button variant="text" onClick={handleClose} disabled={uploadCostPrices.isPending}>
          Cancel
        </Button>
      </DialogActions>
    </Dialog>
  );
}
